import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "../../api/client";
import { resendCompanyInvite } from "../../api/companies";
import { PicAccessSection } from "./PicAccess";
import { FdsBaselineSection } from "./FdsBaseline";
import { FdsRecipientsSection } from "./FdsRecipients";
import { PageContainer } from "@kkp/ui";
import { Button } from "@kkp/ui";
import { Card, CardBody, CardHeader, CardTitle } from "@kkp/ui";
import { toast } from "@kkp/ui";
import { formatNumber } from "@kkp/app-kit";
import {
  COMPANY_DETAIL_KEY,
  Field,
  formatDateTime,
  formatIdr,
  type CompanyDetail,
} from "@kkp/features";

function fetchCompanyDetail(id: string): Promise<CompanyDetail> {
  return apiRequest<CompanyDetail>({
    path: `/company/${id}`,
    method: "GET",
  });
}

export default function CompanyDetailPage() {
  const params = useParams();
  const companyId = params.companyId ?? "";

  const { data, isError, error, isLoading } = useQuery({
    queryKey: COMPANY_DETAIL_KEY(companyId),
    queryFn: () => fetchCompanyDetail(companyId),
    enabled: Boolean(companyId),
    refetchOnMount: "always",
  });

  const detail = data;

  return (
    <PageContainer
      title="Company detail"
      description={companyId ? `ID: ${companyId}` : "No company ID provided."}
      width="wide"
      padding="lg"
    >
      {isError ? (
        <Card>
          <CardBody>
            <div className="text-sm text-[color:var(--kkp-text-muted)]">
              Failed to load company detail.{" "}
              {String((error as Error)?.message ?? "")}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {isLoading ? (
        <Card>
          <CardBody>
            <div className="text-sm text-[color:var(--kkp-text-muted)]">
              Loading…
            </div>
          </CardBody>
        </Card>
      ) : null}

      {detail ? (
        <Card>
          <CardHeader className="flex items-center justify-between gap-3">
            <CardTitle as="h2">{detail.companyLegalName}</CardTitle>
            <ResendInviteAction
              companyId={detail.companyId}
              isOnboarded={detail.picStatus !== "pending"}
            />
          </CardHeader>
          <CardBody className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            <Field label="Company legal name" value={detail.companyLegalName} />{" "}
            <Field label="Back-office ID" value={detail.backOfficeId} />
            <Field label="Tax ID (NPWP)" value={detail.taxId} />
            <Field label="Legal address" value={detail.legalAddress} />
            <Field label="Source bank code" value={detail.sourceBankCode} />
            <Field
              label="Source bank account"
              value={detail.sourceBankAccount}
            />
            <Field
              label="Per batch limit (IDR)"
              value={formatIdr(detail.perBatchLimitIdr)}
            />
            <Field
              label="Per transaction fee (IDR)"
              value={formatIdr(detail.perTransactionFeeIdr)}
            />
            <Field label="PIC fullname" value={detail.picFullname} />
            <Field label="PIC phonenumber" value={detail.picPhonenumber} />
            <Field label="PIC email" value={detail.picEmail} />
            <Field
              label="Reason/description"
              value={detail.reasonDescription}
            />
            <Field
              label="Onboarded at"
              value={formatDateTime(detail.onboardedAt)}
            />
            <Field label="Onboarded by" value={detail.onboardedBy} />
            <Field label="Status" value={detail.status} />
            <Field
              label="Total recipient"
              value={formatNumber(detail.totalRecipient)}
            />
            <Field
              label="Total transactions"
              value={formatNumber(detail.totalTransactions)}
            />
            <Field
              label="Total transactions amount (IDR)"
              value={formatIdr(detail.totalTransactionsAmountIdr)}
            />
            <Field
              label="Total fees (IDR)"
              value={formatIdr(detail.totalFeesIdr)}
            />
          </CardBody>
        </Card>
      ) : null}

      {companyId ? <PicAccessSection companyId={companyId} /> : null}
      {companyId ? <FdsBaselineSection companyId={companyId} /> : null}
      {companyId ? <FdsRecipientsSection companyId={companyId} /> : null}
    </PageContainer>
  );
}

/**
 * Resend the PIC invite email.
 *
 * The backend is authoritative on whether resend is needed — if the PIC is
 * already active, the server returns `{ sent: false, warning }` instead of
 * an error, so this component does not need to inspect PIC status itself.
 */
function ResendInviteAction({
  companyId,
  isOnboarded,
}: {
  companyId: string;
  isOnboarded: boolean;
}) {
  const mutation = useMutation({
    mutationFn: () => resendCompanyInvite(companyId),
    onSuccess: (res) => {
      if (res.sent) {
        toast.success("Invite email resent.", { title: "Invite sent" });
      } else {
        toast.warning(res.warning ?? "Invite was not sent.", {
          title: "Nothing to send",
        });
      }
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : "Failed to resend invite.";
      toast.danger(message, { title: "Resend failed" });
    },
  });

  return (
    <Button
      variant="primary"
      size="sm"
      disabled={isOnboarded || mutation.isPending}
      title={
        isOnboarded ? "PIC already onboarded — resend not needed." : undefined
      }
      onClick={() => mutation.mutate()}
    >
      {mutation.isPending ? "Sending…" : "Resend invite"}
    </Button>
  );
}
